import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Capacitor } from '@capacitor/core'
import { Haptics } from '@capacitor/haptics'
import {
  PermissionStatus,
  PushNotifications,
  PushNotificationSchema,
  RegistrationError,
  Token,
} from '@capacitor/push-notifications'
import { ToastButton, ToastController } from '@ionic/angular'
import { TranslateService } from '@ngx-translate/core'
import { take, takeUntil } from 'rxjs'
import { AppRoute } from 'src/app/app.routes.fixtures'
import { HomeItemRoute } from 'src/app/pages/home/home.routes'
import { ConstantsUtility } from 'src/app/utility/constants.utility'
import { environment } from 'src/environments/environment'
import { AbstractChatService } from '../abstract-chat/abstract-chat.service'
import { AuthService } from '../auth/auth.service'
import { ChatRoomService } from '../chat-room/chat-room.service'
import { ChatUnreadMessageService } from '../chat-unread-message/chat-unread-message.service'
import { SettingsService } from '../settings/settings.service'

@Injectable({
  providedIn: 'root',
})
export class NotificationService extends AbstractChatService {
  private retryAddingToken: string = undefined
  private receivedNotificationIds: string[] = []
  private isRegistered = false

  isNotificationsAvailable = Capacitor.isPluginAvailable('PushNotifications')

  constructor(
    private httpClient: HttpClient,
    private settingsService: SettingsService,
    private chatRoomService: ChatRoomService,
    private unreadMessageService: ChatUnreadMessageService,
    private translateService: TranslateService,
    private toastController: ToastController,
    private router: Router,
    authService: AuthService
  ) {
    super(authService)

    settingsService.isAnyNotificationsEnabled
      .pipe(takeUntil(this.destroySubject))
      .subscribe((isEnabled) => {
        if (isEnabled) {
          this.addListeners()
          this.initialize()
        } else {
          this.removeTokenFromRocketChat()
          if (!this.isNotificationsAvailable) {
            return
          }
          PushNotifications.removeAllListeners()
          PushNotifications.unregister()
          this.isRegistered = false
        }
      })
  }

  override didFetchUserData(): void {
    if (this.retryAddingToken) {
      this.addTokenToRocketChat(this.retryAddingToken)
    }
  }

  initialize() {
    this.checkPermission()
  }

  checkPermission(): Promise<PermissionStatus> {
    if (!this.isNotificationsAvailable) {
      return undefined
    }
    const promise = PushNotifications.checkPermissions()
    promise.then((result) => {
      this.handleRequestResult(result)
    })
    return promise
  }

  async requestPermission(): Promise<PermissionStatus> {
    if (!this.isNotificationsAvailable) {
      return undefined
    }
    const status = await PushNotifications.requestPermissions()
    await this.handleRequestResult(status)

    return status
  }

  private async handleRequestResult(result: PermissionStatus) {
    if (result.receive === 'granted') {
      this.register()
    } else if (result.receive === 'denied') {
      this.settingsService.setIsNotificationsEnabled(false)
    }
  }

  private async register() {
    if (!this.isNotificationsAvailable) {
      return
    }
    if (this.isRegistered) {
      // already registered
      return
    }
    await PushNotifications.register()
    this.isRegistered = true
  }

  private addListeners() {
    if (!this.isNotificationsAvailable) {
      return
    }
    PushNotifications.addListener('registration', (token: Token) => {
      this.addTokenToRocketChat(token.value)
    })
    PushNotifications.addListener(
      'registrationError',
      (error: RegistrationError) => {
        this.isRegistered = false
        console.log('Push notification registration error', error)
      }
    )

    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification) => {
        if (this.receivedNotificationIds.includes(notification.id)) {
          return
        }
        this.receivedNotificationIds.push(notification.id)
        this.handleChatNotification(notification)
      }
    )
  }

  private handleChatNotification(notification: PushNotificationSchema) {
    const roomId = this.extractRoomId(notification)

    if (this.router.url.includes(roomId)) {
      // no need to notify, if user is viewing this chatroom
      return
    }

    Haptics.vibrate()
    this.showToast(
      {
        header: this.translateService.instant('chat.newMessage'),
        message: notification.body,
      },
      ConstantsUtility.toastDuration.long,
      'mail-unread',
      [
        {
          icon: 'arrow-forward-circle-outline',
          handler: () => {
            if (roomId && !this.router.url.includes(roomId)) {
              this.router.navigate([
                `/${AppRoute.chat}`,
                {
                  chatRoomId: roomId,
                },
              ])
              return
            }

            if (!this.router.url.includes(HomeItemRoute.chat)) {
              this.router.navigate([`${AppRoute.home}/${HomeItemRoute.chat}`])
            }
          },
        },
      ]
    )
    if (roomId) {
      this.chatRoomService.fetchChatRoom(roomId, true)
    } else {
      this.chatRoomService.fetchChatRooms()
    }
    this.unreadMessageService.fetchUnreadMessagesCount()
  }

  private extractRoomId(
    notification: PushNotificationSchema
  ): string | undefined {
    if (!notification?.data?.ejson) {
      return undefined
    }
    try {
      const json = JSON.parse(notification?.data?.ejson)
      return json?.rid
    } catch (error) {
      return undefined
    }
  }

  private addTokenToRocketChat(token: string) {
    if (!this.chatUserData) {
      this.retryAddingToken = token
      return
    }

    const url = environment.chatBasePath + '/push.token'
    const postData = {
      type: 'gcm',
      value: token,
      appName: ConstantsUtility.appName,
    }

    this.httpClient
      .post<any>(url, postData, { headers: this.getAuthHeaders() })
      .pipe(take(1))
      .subscribe((result) => {
        if (result.success) {
          this.retryAddingToken = undefined
          this.settingsService.setPushNotificationToken(token)
        }
      })
  }

  private removeTokenFromRocketChat() {
    if (!this.chatUserData) {
      return
    }
    const url = environment.chatBasePath + '/push.token'
    const postData = {
      token: this.settingsService.getPushNotificationToken(),
    }
    this.settingsService.setPushNotificationToken(undefined)

    this.httpClient
      .delete<any>(url, { headers: this.getAuthHeaders(), body: postData })
      .pipe(take(1))
      .subscribe()
  }

  private showToast(
    content: {
      header?: string
      message?: string
    },
    duration: number,
    icon: string,
    buttons: ToastButton[] = []
  ) {
    this.toastController.dismiss()
    this.toastController
      .create({
        header: content.header,
        message: content.message,
        color: 'secondary',
        position: 'top',
        duration: duration,
        icon: icon,
        swipeGesture: 'vertical',
        buttons: buttons,
      })
      .then((toast) => {
        toast.present()
      })
  }
}
